import { Fragment, useMemo, useState, type ReactElement } from 'react';
import { ChevronDown, ChevronRight, ClipboardList, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import {
  DataTable,
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableToolbar,
  DataTableTr,
} from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useBrowseQuestionnaires } from '@/features/browse-questionnaires/api';
import type { QuestionnaireStatusCode } from '@/features/browse-questionnaires/endpoints';
import { QuestionnaireAttachmentsList } from '@/features/patient-record/components/_shared/QuestionnaireAttachmentsList';
import { DeliveryStatusBadge } from '@/features/patient-record/components/_shared/QuestionnaireDeliveryStatus';
import { useAdminQuestionnaireTemplates } from '@/features/admin-questionnaire-templates/api';
import { useAdminUsers } from '@/features/admin-users/api';
import {
  resolvedNameLabel,
  useResolveUserDisplayNames,
} from '@/features/patient-record/components/panels/CareTeamPanel/utils';
import { useAuth } from '@/auth/useAuth';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { questionnaireStatusToBadgeVariant } from '@/lib/clinicalBadges';
import { formatDate } from '@/lib/format';

const DEFAULT_PAGE_SIZE = 50;
const AUTO_REFRESH_MS = 10000;
const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const SEARCH_DEBOUNCE_MS = 300;
const CREATOR_OPTIONS_LIMIT = 100;
const EMPTY_CELL = '—';
const STATUSES: readonly QuestionnaireStatusCode[] = [
  'NOT_SENT',
  'SENT',
  'OPENED',
  'COMPLETED',
  'EXPIRED',
  'REVOKED',
];

export function BrowseQuestionnairesTab({ autoRefresh = false }: { readonly autoRefresh?: boolean }): ReactElement {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [status, setStatus] = useState<QuestionnaireStatusCode | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);
  // Default the "Created by" filter to the current user (their guid == the request author guid).
  const [createdBy, setCreatedBy] = useState<string | null>(() => user?.id ?? null);
  const [searchInput, setSearchInput] = useState('');
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  function toggleExpanded(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  const templatesQuery = useAdminQuestionnaireTemplates();
  const templates = useMemo(
    () => (templatesQuery.data ?? []).filter((tmpl) => tmpl.active),
    [templatesQuery.data],
  );

  // Users for the "Created by" dropdown; ensure the current user is always selectable even if
  // they're not on the first page of the (capped) user list.
  const usersQuery = useAdminUsers({ page: 0, size: CREATOR_OPTIONS_LIMIT });
  const creatorOptions = useMemo(() => {
    const list = (usersQuery.data?.rows ?? []).map((u) => ({
      value: u.uuid,
      label: u.fullName.length > 0 ? u.fullName : u.name,
    }));
    if (user !== null && !list.some((o) => o.value === user.id)) {
      list.unshift({ value: user.id, label: user.name });
    }
    return list;
  }, [usersQuery.data, user]);

  const query = useBrowseQuestionnaires(
    page,
    size,
    {
      status,
      templateId,
      q: debouncedSearch,
      createdBy,
    },
    autoRefresh ? AUTO_REFRESH_MS : false,
  );

  const totalElements = query.data?.totalElements ?? 0;
  const rows = query.data?.rows ?? [];

  // `created_by` is a raw user guid; resolve each to a display name via GET /staff/{uuid}.
  const creatorNames = useResolveUserDisplayNames(rows.map((row) => row.createdBy ?? ''));

  // No creator or lookup in flight → "—"; lookup failed → "Unknown user".
  function creatorLabel(createdBy: string | null): string {
    if (createdBy === null || createdBy.length === 0) return EMPTY_CELL;
    return (
      resolvedNameLabel(creatorNames.get(createdBy), t('patient.careTeam.unknownUser')) ?? EMPTY_CELL
    );
  }

  function resetToFirstPage(): void {
    setPage(0);
  }

  const from = totalElements === 0 ? 0 : page * size + 1;
  const to = page * size + rows.length;

  return (
    <div className="px-8 py-5 space-y-4 bg-background">
        <div
          className="flex flex-wrap items-end gap-3"
          role="search"
          aria-label={t('routes.browseQuestionnaires.searchAria')}
        >
          <div className="relative flex-1 min-w-[180px] max-w-sm">
            <Search
              className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground pointer-events-none"
              aria-hidden="true"
            />
            <Input
              type="search"
              className="pl-8 h-9 bg-card border-border text-sm"
              placeholder={t('routes.browseQuestionnaires.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                resetToFirstPage();
              }}
              aria-label={t('routes.browseQuestionnaires.searchPlaceholder')}
            />
          </div>
          <Select
            value={templateId ?? 'all'}
            onChange={(e) => {
              setTemplateId(e.target.value === 'all' ? null : e.target.value);
              resetToFirstPage();
            }}
            className="h-9 w-56 text-sm"
            aria-label={t('routes.browseQuestionnaires.filters.template')}
          >
            <option value="all">{t('routes.browseQuestionnaires.filters.allTemplates')}</option>
            {templates.map((tmpl) => (
              <option key={tmpl.id} value={tmpl.id}>
                {tmpl.label}
              </option>
            ))}
          </Select>
          <Select
            value={status ?? 'all'}
            onChange={(e) => {
              setStatus(e.target.value === 'all' ? null : (e.target.value as QuestionnaireStatusCode));
              resetToFirstPage();
            }}
            className="h-9 w-40 text-sm"
            aria-label={t('routes.browseQuestionnaires.filters.status')}
          >
            <option value="all">{t('routes.browseQuestionnaires.filters.allStatuses')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`patient.questionnaires.status.${s}`)}
              </option>
            ))}
          </Select>
          <Select
            value={createdBy ?? 'all'}
            onChange={(e) => {
              setCreatedBy(e.target.value === 'all' ? null : e.target.value);
              resetToFirstPage();
            }}
            className="h-9 w-48 text-sm"
            aria-label={t('routes.browseQuestionnaires.filters.createdBy')}
          >
            <option value="all">{t('routes.browseQuestionnaires.filters.allCreators')}</option>
            {creatorOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          {query.isSuccess && (
            <span className="ml-auto text-[12px] text-muted-foreground tabular-nums">
              {t('routes.browseQuestionnaires.count', { count: totalElements })}
            </span>
          )}
        </div>

        {query.isPending && <BrowseQuestionnairesSkeleton />}

        {query.isError && (
          <div className="max-w-xl">
            <FetchErrorCallout
              error={query.error}
              heading={t('routes.browseQuestionnaires.error.heading')}
              message={t('routes.browseQuestionnaires.error.message')}
              codeLabel={t('routes.browseQuestionnaires.error.codeLabel')}
              onRetry={() => {
                query.refetch().catch(() => undefined);
              }}
              retryLabel={t('routes.browseQuestionnaires.error.retry')}
            />
          </div>
        )}

        {query.isSuccess && rows.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-16 text-center" role="status">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
              <ClipboardList className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </div>
            <p className="text-sm text-muted-foreground">
              {t('routes.browseQuestionnaires.empty')}
            </p>
          </div>
        )}

        {query.isSuccess && rows.length > 0 && (
          <DataTable aria-label={t('routes.browseQuestionnaires.heading')}>
            <DataTableToolbar
              pageSize={size}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageSizeChange={(next) => {
                setSize(next);
                resetToFirstPage();
              }}
              from={from}
              to={to}
              total={totalElements}
              onPagePrevious={() => {
                setPage((p) => Math.max(0, p - 1));
              }}
              onPageNext={() => {
                setPage((p) => p + 1);
              }}
              canGoPrevious={page > 0}
              canGoNext={to < totalElements}
              pageSizeSelectId="browse-questionnaires-page-size"
            />
            <DataTableTable aria-label={t('routes.browseQuestionnaires.heading')}>
              <DataTableThead>
                <DataTableHeaderRow>
                  <DataTableTh widthClassName="w-[20%] min-w-[8rem]">
                    {t('routes.browseQuestionnaires.table.patient')}
                  </DataTableTh>
                  <DataTableTh widthClassName="w-[20%] min-w-[8rem]">
                    {t('routes.browseQuestionnaires.table.template')}
                  </DataTableTh>
                  <DataTableTh widthClassName="w-40 min-w-[8rem]">
                    {t('routes.browseQuestionnaires.table.createdBy')}
                  </DataTableTh>
                  <DataTableTh widthClassName="w-24">
                    {t('routes.browseQuestionnaires.table.delivery')}
                  </DataTableTh>
                  <DataTableTh className="whitespace-nowrap" widthClassName="w-32">
                    {t('routes.browseQuestionnaires.table.sentDate')}
                  </DataTableTh>
                  <DataTableTh widthClassName="w-28">
                    {t('routes.browseQuestionnaires.table.status')}
                  </DataTableTh>
                  <DataTableTh widthClassName="w-28">
                    {t('routes.browseQuestionnaires.table.deliveryStatus')}
                  </DataTableTh>
                  <DataTableTh className="whitespace-nowrap" widthClassName="w-32">
                    {t('routes.browseQuestionnaires.table.completedOn')}
                  </DataTableTh>
                  <DataTableTh className="tabular-nums" widthClassName="w-20" align="right">
                    {t('routes.browseQuestionnaires.table.daysOut')}
                  </DataTableTh>
                </DataTableHeaderRow>
              </DataTableThead>
              <DataTableTbody>
                {rows.map((row) => {
                  const isOpen = expanded.has(row.id);
                  return (
                  <Fragment key={row.id}>
                  <DataTableTr
                    className="cursor-pointer"
                    onClick={() => {
                      toggleExpanded(row.id);
                    }}
                  >
                    <DataTableTd className="pr-1 font-medium text-foreground" dataOffset={false}>
                      <span className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleExpanded(row.id);
                          }}
                          aria-expanded={isOpen}
                          aria-label={t('patient.questionnaires.attachments.toggle')}
                          className="shrink-0 text-muted-foreground hover:text-foreground"
                        >
                          {isOpen ? (
                            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                          )}
                        </button>
                        {row.personGuid.length > 0 ? (
                          <Link
                            to={`/patients/${row.personGuid}#questionnaires`}
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                            className="text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                          >
                            {row.patientName ?? t('patient.header.unknownName')}
                          </Link>
                        ) : (
                          (row.patientName ?? EMPTY_CELL)
                        )}
                      </span>
                    </DataTableTd>
                    <DataTableTd className="text-foreground/90 truncate" dataOffset>
                      {row.templateName ?? (row.templateId.length > 0 ? row.templateId : EMPTY_CELL)}
                    </DataTableTd>
                    <DataTableTd className="text-foreground/90 truncate" dataOffset>
                      {creatorLabel(row.createdBy)}
                    </DataTableTd>
                    <DataTableTd className="text-foreground/90" dataOffset>
                      {row.deliveryMethod !== null
                        ? t(`patient.questionnaires.delivery.${row.deliveryMethod}`)
                        : EMPTY_CELL}
                    </DataTableTd>
                    <DataTableTd className="whitespace-nowrap text-foreground/90" dataOffset>
                      {row.sentAt !== null ? formatDate(row.sentAt) : EMPTY_CELL}
                    </DataTableTd>
                    <DataTableTd dataOffset>
                      <Badge
                        variant={questionnaireStatusToBadgeVariant(row.status)}
                        className="h-5 px-2 rounded-full text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap"
                      >
                        {t(`patient.questionnaires.status.${row.status}`)}
                      </Badge>
                    </DataTableTd>
                    <DataTableTd dataOffset>
                      {row.deliveryStatus !== null ? (
                        <DeliveryStatusBadge status={row.deliveryStatus} />
                      ) : (
                        EMPTY_CELL
                      )}
                    </DataTableTd>
                    <DataTableTd className="whitespace-nowrap text-foreground/90" dataOffset>
                      {row.completedAt !== null ? formatDate(row.completedAt) : EMPTY_CELL}
                    </DataTableTd>
                    <DataTableTd className="tabular-nums text-foreground/90" align="right" dataOffset>
                      {row.daysOut ?? EMPTY_CELL}
                    </DataTableTd>
                  </DataTableTr>
                  {isOpen && (
                    <DataTableTr>
                      <DataTableTd colSpan={9} className="bg-muted/30">
                        <div className="pl-5">
                          {row.personGuid.length > 0 ? (
                            <QuestionnaireAttachmentsList
                              guid={row.personGuid}
                              requestGuid={row.id}
                            />
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              {t('patient.questionnaires.attachments.empty')}
                            </p>
                          )}
                        </div>
                      </DataTableTd>
                    </DataTableTr>
                  )}
                  </Fragment>
                  );
                })}
              </DataTableTbody>
            </DataTableTable>
          </DataTable>
        )}
    </div>
  );
}

const SKELETON_ROWS = 8;
const SKELETON_CELL_WIDTHS = ['w-28', 'w-24', 'w-24', 'w-12', 'w-16', 'w-16', 'w-16', 'w-16', 'w-10'] as const;

/** Layout-stable placeholder; column widths mirror the real table so loading → loaded doesn't reflow. */
function BrowseQuestionnairesSkeleton(): ReactElement {
  const { t } = useTranslation();
  return (
    <div role="status" aria-busy="true" aria-label={t('routes.browseQuestionnaires.loading')} className="space-y-2">
      {Array.from({ length: SKELETON_ROWS }, (_, rowIdx) => (
        <div key={rowIdx} className="flex items-center gap-4 rounded-md border border-border bg-card px-4 py-3">
          {SKELETON_CELL_WIDTHS.map((width, cellIdx) => (
            <Skeleton key={cellIdx} className={`h-3.5 ${width}`} />
          ))}
        </div>
      ))}
    </div>
  );
}
